import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import axios from "axios";
import { get as _get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useNotification } from "~/Hooks/useNotification";
import { eramanCMSApi, post } from "~/Services";
import { eramanHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import {
  renderActionsColumn,
  renderDateColumn,
  renderImageColumn,
  renderPublishedColumn,
} from "~/Utils/TableRenderFunction";
import { useOutletDocumentDetails } from "./Outlet.hook";

export const Outlet = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [pageSize, setPageSize] = useState<number>(10);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const publishedData: TypeData[] = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const { data: drilldown, refetch: refetchDocument } = useOutletDocumentDetails(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    if (query.get("subActiveTab") === "outlet") {
      setSelectedPublished("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("subActiveTab")]);

  useEffect(() => {
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = [...safeData];

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter(
        (item: any) =>
          String(item.name || "")
            .toLowerCase()
            .includes(searchLower)
        // String(item.field_location_airports || "").toLowerCase().includes(searchLower) ||
        // String(item.field_location_level_name || "").toLowerCase().includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      filteredData = filteredData.filter(
        (item: any) => item.published === selectedPublished || item.status === selectedPublished
      );
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  // ✅ Get All
  const getRecords = async () => {
    setIsLoading(true);
    const url = `/api/location-map-stores-data/all-fields`;

    try {
      const response = await eramanCMSApi.get(url);
      if (Array.isArray(response?.data)) {
        setData(response.data);
        setTotalRecords(response.data.length);
      } else {
        setData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching outlet data:", error);
      setData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Create / Update
  // const onSubmit = async (formData: FieldValues, isEdit: boolean, editId?: string) => {
  //   try {
  //     if (isEdit && editId) {
  //       await axios.patch(
  //         `${eramanHost}/api/get-location-map-stores-crud-data/${editId}`,
  //         formData
  //       );
  //       openToast({ content: "Outlet updated successfully", type: "success" });
  //     } else {
  //       await axios.post(
  //         `${eramanHost}/api/create-location-map-stores-crud-data`,
  //         formData
  //       );
  //       openToast({ content: "Outlet created successfully", type: "success" });
  //     }
  //     getRecords();
  //     setDrawerVisible(false);
  //   } catch (error) {
  //     console.error("Error saving outlet:", error);
  //     openToast({ content: "Failed to save outlet", type: "error" });
  //   }
  // };

  // ✅ Delete
  // async function sendDeleteBrandRequest() {
  //   try {
  //     await axios.delete(
  //       `${eramanHost}/api/get-location-map-stores-crud-data/${selectedRowData?.id}`
  //     );
  //     openToast({ content: "Outlet deleted successfully", type: "success" });
  //     getRecords();
  //   } catch (error) {
  //     console.error("Delete error:", error);
  //     openToast({ content: "Failed to delete outlet", type: "error" });
  //   }
  // }

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Store Location",
      subModule: "outlet",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        navigate("/dedicated/status-information?activeTab=pending");
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteAwardsRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${eramanHost}/api/get-location-map-stores-crud-data/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }

  // ✅ Get By Id (helper for drawer if needed)
  const getOutletById = async (outletId: string) => {
    try {
      const response = await axios.get(`${eramanHost}/api/location-map-stores-data/all-fields/${outletId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching outlet by ID:", error);
      return null;
    }
  };

  const columns: TableColumnsType = [
    {
      title: "Main Image",
      dataIndex: "field_store_description_image",
      key: "field_store_description_image",
      render: renderImageColumn,
    },
    // {
    //   title: "Name",
    //   dataIndex: "name",
    //   key: "name",
    //   render: (text) => decodeHTML(text),
    //   sorter: true,
    // },
    {
      title: "Outlet",
      dataIndex: "field_stores_name",
      key: "field_stores_name",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_stores_name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_stores_name"
            ? "descend"
            : undefined,
    },
    {
      title: "Address",
      dataIndex: "field_store_description",
      key: "field_store_description",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_store_description"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_store_description"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Description",
    //   dataIndex: "field_store_description",
    //   key: "field_store_description",
    //   render: (text) => decodeHTML(text),
    // },
    // {
    //   title: "Store Time",
    //   dataIndex: "field_store_time",
    //   key: "field_store_time",
    // },
    // {
    //   title: "Time Icon",
    //   dataIndex: "field_time_icon",
    //   key: "field_time_icon",
    //   render: renderImageColumn,
    // },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "field_store_created_at",
      key: "field_store_created_at",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_store_created_at"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_store_created_at"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "field_store_updated_at",
      key: "field_store_updated_at",
      // render: renderDateColumn,
      render: (value: any, record: any) => {
        const created = record?.field_store_created_at
          ? moment(record.field_store_created_at).format("YYYY-MM-DD HH:mm:ss")
          : null;
        const updated = value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null;

        // if both same -> show null for updated
        if (created && updated && created === updated) {
          return null; // you can also return "-" or <Typography.Text>-</Typography.Text>
        }

        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_store_updated_at"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_store_updated_at"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/eraman/storeLocations/outlet/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
      order = "asc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...data];
    setSortedData(tempData.sort((a, b) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={currentPageData || []}
        isLoading={isLoading}
        exportFileName="OUTLET"
        isPagination={true}
        recordsPerPage={5}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Outlet"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />

      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "outlet") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteAwardsRequest}
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (["field_stores_name", "field_store_description"].includes(sortBy)) {
    const titleA = a[sortBy]?.toLowerCase() || "";
    const titleB = b[sortBy]?.toLowerCase() || "";

    if (order === "desc") {
      return titleA < titleB ? 1 : -1;
    } else if (order === "asc") {
      return titleA > titleB ? 1 : -1;
    } else {
      return 0;
    }
  }

  // if (sortBy === "field_store_updated_at") {
  //   const createdA = a.field_store_created_at || null;
  //   const createdB = b.field_store_created_at || null;

  //   if (a[sortBy] === createdA) a[sortBy] = null;
  //   if (b[sortBy] === createdB) b[sortBy] = null;
  // }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

export function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
